// InterviewReportsPage — HR-facing comprehensive report viewer.
//
// Two modes (route-driven):
//   /jobs/:jobId/reports             → list every report for a job (grouped by round)
//   /jobs/:jobId/reports/:reportId   → detail (transcript + axis scores + narrative)
//
// Laid out as the other EquaOS apps are: a header with the screen's actions,
// figures as stat cards, and each block a card. Built only from the design
// system (src/ds) and the page kit (src/components/page).
import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../services/api";
import { toast } from "react-toastify";
import { Button, Card, EmptyState, Icon, Modal, StatusPill, Table } from "../ds";
import { Page, PageHeader, StatRow, Stat, Section, Loading, Note } from "../components/page";
import { BriefRubricScores, GuardrailFlags, hasBriefScores } from "./assessments/BriefReportScores";
import SomethingWrong from "./assessments/SomethingWrong";
const RECO_STYLE = {
  strong_hire:    { label: "Strong hire",    tone: "success" },
  hire:           { label: "Hire",           tone: "success" },
  borderline:     { label: "Borderline",     tone: "warning" },
  no_hire:        { label: "No hire",        tone: "danger"  },
  strong_no_hire: { label: "Strong no hire", tone: "danger"  },
  time_expired:   { label: "Time expired",   tone: "neutral" },
  no_response:    { label: "No response",    tone: "neutral" },
};
const ROUND_LABEL = {
  preScreening:     "Pre-Screening",
  psychoanalytical: "Psychoanalytical",
  finalInterview:   "Final Interview",
  coding:           "Coding Test",
  gen_ai_readiness: "Gen AI Readiness",
};
// Per-round unique report configuration — icon, description, and named parameter labels.
// These labels map onto axisScores[].axis so the UI can highlight them distinctively.
const ROUND_CONFIG = {
  preScreening: {
    icon: "chat-circle-text",
    title: "Pre-Screening Interview",
    description: "AI-conducted first-touch screen. Evaluates baseline communication, role fit signals, and red-flag detection before deeper assessment.",
    focusAreas: ["Communication Clarity", "Role Fit Signal", "Motivation & Intent", "Culture Alignment", "Articulation Quality"],
    uniqueLabel: "Screening Signals",
  },
  psychoanalytical: {
    icon: "brain",
    title: "Behavioral & Psychoanalytical Interview",
    description: "Structured behavioral interview probing cognitive flexibility, emotional regulation, interpersonal dynamics, and stress response patterns.",
    focusAreas: ["Emotional Intelligence", "Behavioral Consistency", "Self-Awareness", "Conflict Resolution", "Adaptability"],
    uniqueLabel: "Behavioral Patterns",
  },
  finalInterview: {
    icon: "medal",
    title: "Final Interview",
    description: "Comprehensive deep-dive evaluating technical depth, strategic thinking, leadership potential, and overall hiring readiness.",
    focusAreas: ["Technical Depth", "Problem Solving", "Leadership Potential", "Strategic Thinking", "Decision Quality"],
    uniqueLabel: "Hiring Decision Factors",
  },
  coding: {
    icon: "desktop",
    title: "Live Coding Interview",
    description: "Real-time coding session assessing algorithm design, code quality, debugging approach, and engineering reasoning.",
    focusAreas: ["Algorithm Design", "Code Quality", "Problem Decomposition", "Debugging Approach", "Edge Case Handling"],
    uniqueLabel: "Coding Performance",
  },
  gen_ai_readiness: {
    icon: "sparkle",
    title: "Gen AI Readiness Assessment",
    description: "Evaluates practical AI tool fluency, prompt engineering ability, automation mindset, and ethical AI awareness.",
    focusAreas: ["AI Tool Fluency", "Prompt Engineering", "Automation Thinking", "AI Ethics Awareness", "Adoption Velocity"],
    uniqueLabel: "AI Readiness Indicators",
  },
};
// Small layout pieces, drawn only with the design system's variables.
const muted = { color: "var(--text-muted)", fontSize: "var(--fs-caption)" };
const stack = (gap = 12) => ({ display: "grid", gap });
const tiles = (min = 160) => ({ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`, gap: 12 });
const tile = { background: "var(--surface-2)", borderRadius: "var(--radius-md)", padding: 12 };
const tileLabel = { ...muted, marginBottom: 4 };
const tileValue = { fontSize: "var(--fs-body-lg)", fontWeight: 600, color: "var(--text-1)" };
const sub = { fontSize: "var(--fs-body-sm)", fontWeight: 600, color: "var(--text-2)", marginBottom: 8 };
const scoreTone = (v) => (v >= 75 ? "success" : v >= 50 ? "warning" : "danger");
const toneText = (tone) => `var(--status-${tone}-text)`;
// Screen-only parts (the back and export buttons) stay off the printed PDF,
// and the transcript prints in full.
const PRINT_CSS = `
  .ir-transcript{max-height:60vh;overflow-y:auto}
  @media print{.ir-noprint{display:none!important}.ir-transcript{max-height:none;overflow:visible}}
`;
function RoundHero({ report }) {
  const cfg = ROUND_CONFIG[report.round];
  if (!cfg) return null;
  // Separate axis scores that match a focus area from generic axes
  const focusScores = (report.axisScores || []).filter(a =>
    cfg.focusAreas.some(f => a.axis?.toLowerCase().includes(f.split(" ")[0].toLowerCase()))
  );
  // Fall back to first 3 axis scores if none matched by name
  const displayScores = focusScores.length > 0 ? focusScores : (report.axisScores || []).slice(0, 3);
  return (
    <Section title={<span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><Icon name={cfg.icon} size={20} />{cfg.title}</span>}>
      <div style={stack(16)}>
        <p style={{ margin: 0, color: "var(--text-2)", fontSize: "var(--fs-body-sm)", lineHeight: "var(--lh-body)" }}>{cfg.description}</p>
        {/* Focus areas as tags */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {cfg.focusAreas.map(f => (
            <StatusPill key={f} tone="info" dot={false}>{f}</StatusPill>
          ))}
        </div>
        {/* Top axis scores inline for quick scan */}
        {displayScores.length > 0 && (
          <div>
            <div style={sub}>{cfg.uniqueLabel}</div>
            <div style={tiles(180)}>
              {displayScores.map((a, i) => (
                <div key={i} style={tile}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 6 }}>
                    <span style={{ fontSize: "var(--fs-caption)", fontWeight: 600, color: "var(--text-2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.axis}</span>
                    <span className="num" style={{ fontWeight: 700, color: "var(--text-1)" }}>{a.score ?? "—"}</span>
                  </div>
                  <ScoreBar value={a.score} />
                  {a.note && <div style={{ ...muted, marginTop: 4, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{a.note}</div>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Section>
  );
}
function Badge({ reco }) {
  const s = RECO_STYLE[reco] || RECO_STYLE.borderline;
  return <StatusPill tone={s.tone}>{s.label}</StatusPill>;
}
function ScoreChip({ value }) {
  if (value == null) return <span style={{ color: "var(--text-muted)" }}>—</span>;
  const v = Math.max(0, Math.min(100, value));
  return <StatusPill tone={scoreTone(v)} dot={false}>{v}</StatusPill>;
}
function ScoreBar({ value }) {
  const v = Math.max(0, Math.min(100, value || 0));
  return (
    <div style={{ width: "100%", height: 8, background: "var(--chart-track)", borderRadius: "var(--radius-pill)", overflow: "hidden" }}>
      <div style={{ height: "100%", width: `${v}%`, background: toneText(scoreTone(v)), borderRadius: "var(--radius-pill)" }} />
    </div>
  );
}
// ── Total AI cost (shown above groups) ──────────────────────────────
function CostBanner({ jobId, reports }) {
  const [jobCost, setJobCost] = useState(null);
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    if (!jobId) return;
    api.get(`/reports/job/${jobId}/total-cost`)
      .then(r => setJobCost(r.data?.data || null))
      .catch(() => {});
  }, [jobId]);
  const fallbackUsd    = reports.reduce((s, r) => s + (r.cost?.usd    || 0), 0);
  const fallbackTokens = reports.reduce((s, r) => s + (r.cost?.tokens || 0), 0);
  const totalUsd       = jobCost ? jobCost.totalUsd    : fallbackUsd;
  const totalTokens    = jobCost ? jobCost.totalTokens : fallbackTokens;
  const candidates     = jobCost ? jobCost.candidateCount : new Set(reports.map(r => r.resumeId || r.candidateEmail)).size;
  const byStage        = jobCost?.byStage || [];
  const perCandidate   = candidates > 0 ? totalUsd / candidates : 0;
  if (totalUsd === 0 && totalTokens === 0) return null;
  const stageRows = [
    ...byStage.map((s, i) => ({ id: `s${i}`, stage: s.stage, count: s.count, tokens: s.tokens.toLocaleString(), usd: `$${s.usdCost.toFixed(4)}` })),
    { id: "total", total: true, stage: "Total", count: byStage.reduce((s, e) => s + e.count, 0), tokens: totalTokens.toLocaleString(), usd: `$${totalUsd.toFixed(4)}` },
  ];
  const strong = (r, v) => (r.total ? <strong style={{ color: "var(--text-1)" }}>{v}</strong> : v);
  return (
    <>
      <StatRow>
        <Stat label="Total AI Cost (all operations)" value={`$${totalUsd.toFixed(4)}`} />
        <Stat label="Tokens consumed" value={totalTokens.toLocaleString()} />
        <Stat label="Avg per candidate" value={`$${perCandidate.toFixed(4)}`} />
        <Stat label="Candidates" value={candidates} />
      </StatRow>
      {byStage.length > 0 && (
        <Section
          title="Cost breakdown"
          subtitle="JD generation · Resume screening · Interviews · Assessments · Offer letters"
          action={(
            <Button size="sm" variant="ghost" aria-expanded={expanded} onClick={() => setExpanded(e => !e)}
              icon={<Icon name="caret-down" style={{ transform: expanded ? "rotate(180deg)" : "none" }} />} iconPosition="right">
              {expanded ? "Hide" : "View"} breakdown
            </Button>
          )}
        >
          {expanded && (
            <div style={{ margin: "0 -20px -20px" }}>
            <Table
              rows={stageRows}
              columns={[
                { key: "stage", label: "Stage / Operation", render: (r) => strong(r, r.stage) },
                { key: "count", label: "Ops", render: (r) => strong(r, r.count) },
                { key: "tokens", label: "Tokens", render: (r) => strong(r, r.tokens) },
                { key: "usd", label: "Cost (USD)", render: (r) => strong(r, r.usd) },
              ]}
            />
            </div>
          )}
        </Section>
      )}
    </>
  );
}
// ── The heading of one collapsible group: its name, its figures, open/closed ──
function GroupHeader({ open, onToggle, label, count, pills }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      style={{
        appearance: "none", background: "none", border: "none", cursor: "pointer", width: "100%",
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap",
        padding: "18px 20px", font: "inherit", textAlign: "left",
      }}
    >
      <span style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
        <span style={{ fontSize: "var(--fs-display-sm)", fontWeight: 600, color: "var(--text-1)" }}>{label}</span>
        <span style={{ fontSize: "var(--fs-body-sm)", color: "var(--text-muted)" }}>{count} report{count !== 1 ? "s" : ""}</span>
      </span>
      <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
        {pills}
        <Icon name={open ? "caret-down" : "caret-right"} style={{ color: "var(--text-muted)" }} />
      </span>
    </button>
  );
}
const strip = { display: "flex", gap: 8, flexWrap: "wrap", padding: "12px 20px", borderTop: "1px solid var(--border)" };
// ── Single collapsible group ───────────────────────────────────────────
function RoundGroup({ round, label, reports, jobId, nav, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  // Summary metrics
  const count      = reports.length;
  const scored     = reports.filter(r => r.overallScore != null);
  const avgScore   = scored.length
    ? Math.round(scored.reduce((s, r) => s + r.overallScore, 0) / scored.length)
    : null;
  const hires      = reports.filter(r => r.recommendation === "hire" || r.recommendation === "strong_hire").length;
  const hireRate   = Math.round((hires / count) * 100);
  const recoGroups = {};
  reports.forEach(r => { recoGroups[r.recommendation] = (recoGroups[r.recommendation] || 0) + 1; });
  const roundUsd   = reports.reduce((s, r) => s + (r.cost?.usd || 0), 0);
  const roundLabel = (label || ROUND_LABEL[round] || round).replace(/\s*\(Arya\)/gi, '');
  const columns = [
    { key: "candidate", label: "Candidate", render: (r) => (
      <div>
        <div style={{ fontWeight: 500, color: "var(--text-1)" }}>{r.candidateName || "—"}</div>
        <div style={muted}>{r.candidateEmail || ""}</div>
      </div>
    ) },
    { key: "modality", label: "Modality", render: (r) => (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: "var(--fs-body-sm)" }}>
        {r.modality === "audio" ? <><Icon name="microphone" />Audio</> : <><Icon name="chat-circle-text" />Text</>}
      </span>
    ) },
    { key: "score", label: "Score", render: (r) => <ScoreChip value={r.overallScore} /> },
    { key: "reco", label: "Recommendation", render: (r) => <Badge reco={r.recommendation} /> },
    { key: "cost", label: "Cost", render: (r) => <span style={muted}>${(r.cost?.usd || 0).toFixed(4)} · {r.cost?.tokens || 0} tk</span> },
    { key: "completed", label: "Completed", render: (r) => <span style={muted}>{new Date(r.completedAt).toLocaleString()}</span> },
  ];
  return (
    <Card padding="0">
      {/* ── Header (always visible) ── */}
      <GroupHeader
        open={open}
        onToggle={() => setOpen(o => !o)}
        label={roundLabel}
        count={count}
        pills={(
          <>
            {avgScore != null && <StatusPill tone="neutral" dot={false}>Avg score: {avgScore}</StatusPill>}
            <StatusPill tone={hireRate >= 50 ? "success" : "danger"} dot={false}>Hire rate: {hireRate}%</StatusPill>
            <StatusPill tone="neutral" dot={false}>Candidates: {count}</StatusPill>
            {roundUsd > 0 && <StatusPill tone="info" dot={false}>AI cost: ${roundUsd.toFixed(4)}</StatusPill>}
          </>
        )}
      />
      {/* ── Recommendation bar (always visible under header) ── */}
      <div style={strip}>
        {Object.entries(recoGroups).map(([reco, cnt]) => {
          const s = RECO_STYLE[reco] || RECO_STYLE.borderline;
          return (
            <StatusPill key={reco} tone={s.tone}>
              {s.label} <strong>{cnt}</strong>
            </StatusPill>
          );
        })}
      </div>
      {/* ── Collapsible candidate table ── */}
      {open && (
        <div style={{ borderTop: "1px solid var(--border)" }}>
          <Table
            columns={columns}
            rows={reports.slice().sort((a, b) => (b.overallScore ?? -1) - (a.overallScore ?? -1))}
            getRowId={(r) => r._id}
            onRowClick={(r) => nav(`/jobs/${jobId}/reports/${r._id}`)}
          />
        </div>
      )}
    </Card>
  );
}
const RISK_TONE = { low: "success", medium: "warning", high: "danger" };
const INTEGRITY_TONE = { clean: "success", suspicious: "warning" };
// ── One candidate's exam details, opened from the assessment table ──────
function ExamDetails({ r }) {
  const ed = r.examDetails || {};
  return (
    <div style={{ ...stack(16), padding: "16px 20px", borderTop: "1px solid var(--border)", background: "var(--surface-2)" }}>
      <div style={{ fontWeight: 600, color: "var(--text-1)" }}>{r.candidateName}</div>
      <div style={tiles(150)}>
        <div style={{ ...tile, background: "var(--surface-1)" }}>
          <div style={tileLabel}>Earned pts</div>
          <div style={tileValue}>{ed.earnedPoints ?? '—'} / {ed.totalPoints ?? '—'}</div>
        </div>
        <div style={{ ...tile, background: "var(--surface-1)" }}>
          <div style={tileLabel}>Time taken</div>
          <div style={tileValue}>{ed.timeTaken ? `${Math.round(ed.timeTaken / 60)}m ${ed.timeTaken % 60}s` : '—'}</div>
        </div>
        <div style={{ ...tile, background: "var(--surface-1)" }}>
          <div style={tileLabel}>Integrity</div>
          <div style={{ ...tileValue, textTransform: "capitalize", color: ed.integrityStatus ? toneText(INTEGRITY_TONE[ed.integrityStatus] || "danger") : "var(--text-1)" }}>
            {ed.integrityStatus || '—'}
          </div>
        </div>
        <div style={{ ...tile, background: "var(--surface-1)" }}>
          <div style={tileLabel}>Integrity score</div>
          <div style={tileValue}>{ed.integrityScore ?? '—'}/100</div>
        </div>
      </div>
      {/* Violations */}
      {(ed.violations || []).length > 0 && (
        <div>
          <div style={sub}>Violations ({ed.violations.length})</div>
          <div style={{ ...stack(4), maxHeight: 144, overflowY: "auto" }}>
            {ed.violations.map((v, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "var(--fs-caption)", background: "var(--status-danger-bg)", borderRadius: "var(--radius-sm)", padding: "6px 12px" }}>
                <span style={{ fontWeight: 600, color: "var(--status-danger-text)", textTransform: "capitalize" }}>{(v.type || '').replace(/_/g, ' ')}</span>
                <span style={{ color: "var(--text-2)" }}>— {v.description}</span>
                <span style={{ marginLeft: "auto", color: "var(--text-muted)" }}>{v.timestamp ? new Date(v.timestamp).toLocaleTimeString() : ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Graded answers */}
      {(ed.gradedAnswers || []).length > 0 && (
        <div>
          <div style={sub}>Graded Answers ({ed.gradedAnswers.length})</div>
          <div style={{ ...stack(8), maxHeight: 256, overflowY: "auto" }}>
            {ed.gradedAnswers.map((a, i) => (
              <div key={i} style={{ fontSize: "var(--fs-caption)", background: a.correct ? "var(--status-success-bg)" : "var(--status-danger-bg)", borderRadius: "var(--radius-sm)", padding: "8px 12px" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontWeight: 600, color: "var(--text-1)" }}>Q{i + 1} · {(a.questionType || 'mcq').replace(/_/g, ' ')}</span>
                  <span style={{ fontWeight: 700, color: toneText(a.correct ? "success" : "danger") }}>
                    {a.earnedPoints ?? 0}/{a.points ?? 10} pts
                  </span>
                </div>
                {a.feedback && <div style={{ color: "var(--text-2)", fontStyle: "italic" }}>{a.feedback}</div>}
                {a.writtenAnswer && <div style={{ color: "var(--text-2)", marginTop: 4 }}>Answer: {a.writtenAnswer}</div>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
// ── Assessment (exam) group ────────────────────────────────────────────
function AssessmentGroup({ group, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const [expandedRow, setExpandedRow] = useState(null);
  const { results = [] } = group;
  const passed     = results.filter(r => r.status === 'shortlisted').length;
  const avgScore   = results.length
    ? Math.round(results.reduce((s, r) => s + (r.score ?? 0), 0) / results.length)
    : null;
  const totalCost  = results.reduce((s, r) => s + (r.cost?.usd || 0), 0);
  const passRate   = Math.round((passed / results.length) * 100);
  const sorted     = results.slice().sort((a, b) => (b.score ?? -1) - (a.score ?? -1));
  const openRow    = sorted.find(r => r.resumeId + r.stageId === expandedRow);
  const columns = [
    { key: "candidate", label: "Candidate", render: (r) => (
      <div>
        <div style={{ fontWeight: 500, color: "var(--text-1)" }}>{r.candidateName}</div>
        <div style={muted}>{r.candidateEmail}</div>
      </div>
    ) },
    { key: "score", label: "Score", render: (r) => (
      <StatusPill tone={r.status === 'shortlisted' ? "success" : "danger"} dot={false}>{r.score ?? '—'}% / {r.passingScore}%</StatusPill>
    ) },
    { key: "result", label: "Result", render: (r) => (
      <StatusPill tone={r.status === 'shortlisted' ? "success" : "danger"}>{r.status === 'shortlisted' ? 'Passed' : 'Failed'}</StatusPill>
    ) },
    { key: "proctoring", label: "Proctoring", render: (r) => {
      const ed = r.examDetails || {};
      return ed.proctoringScore != null
        ? <StatusPill tone={RISK_TONE[ed.riskLevel] || "neutral"} dot={false}>{ed.proctoringScore}/100 · {ed.riskLevel || '—'}</StatusPill>
        : <span style={muted}>—</span>;
    } },
    { key: "violations", label: "Violations", render: (r) => {
      const ed = r.examDetails || {};
      if (ed.violationCount == null) return <span style={muted}>—</span>;
      return <span style={ed.violationCount > 0 ? { color: "var(--status-danger-text)", fontWeight: 600 } : { color: "var(--text-muted)" }}>{ed.violationCount}</span>;
    } },
    { key: "cost", label: "Cost", render: (r) => <span style={muted}>${(r.cost?.usd || 0).toFixed(4)} · {r.cost?.tokens || 0} tk</span> },
    { key: "completed", label: "Completed", render: (r) => <span style={muted}>{r.completedAt ? new Date(r.completedAt).toLocaleString() : '—'}</span> },
    { key: "details", label: "", render: (r) => {
      const rowOpen = expandedRow === r.resumeId + r.stageId;
      return (
        <Button size="sm" variant="ghost" aria-expanded={rowOpen} onClick={() => setExpandedRow(rowOpen ? null : r.resumeId + r.stageId)}>
          {rowOpen ? 'Hide' : 'Details'}
        </Button>
      );
    } },
  ];
  return (
    <Card padding="0">
      {/* Header */}
      <GroupHeader
        open={open}
        onToggle={() => setOpen(o => !o)}
        label={group.label}
        count={results.length}
        pills={(
          <>
            {avgScore != null && <StatusPill tone="neutral" dot={false}>Avg score: {avgScore}%</StatusPill>}
            <StatusPill tone={passRate >= 50 ? "success" : "danger"} dot={false}>Pass rate: {passRate}%</StatusPill>
            <StatusPill tone="neutral" dot={false}>Candidates: {results.length}</StatusPill>
            {totalCost > 0 && <StatusPill tone="info" dot={false}>AI cost: ${totalCost.toFixed(4)}</StatusPill>}
          </>
        )}
      />
      {/* Pass/fail bar */}
      <div style={strip}>
        <StatusPill tone="success">Passed <strong>{passed}</strong></StatusPill>
        <StatusPill tone="danger">Failed <strong>{results.length - passed}</strong></StatusPill>
      </div>
      {open && (
        <div style={{ borderTop: "1px solid var(--border)" }}>
          <Table columns={columns} rows={sorted} getRowId={(r) => r.resumeId + r.stageId} />
          {openRow && <ExamDetails r={openRow} />}
        </div>
      )}
    </Card>
  );
}
// ─────────────────────────────────────────────────────────────────────
//  List view  (grouped by round)
// ─────────────────────────────────────────────────────────────────────
export function JobReportsList() {
  const { jobId } = useParams();
  const nav = useNavigate();
  const [reports, setReports]         = useState([]);
  const [assessments, setAssessments] = useState([]); // exam assessment groups
  const [loading, setLoading]         = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const [rRes, aRes] = await Promise.all([
          api.get(`/reports/job/${jobId}`),
          api.get(`/reports/job/${jobId}/assessments`).catch(() => ({ data: { data: [] } })),
        ]);
        setReports(rRes.data?.data || []);
        setAssessments(aRes.data?.data || []);
      } catch (e) {
        toast.error("Failed to load reports");
      } finally {
        setLoading(false);
      }
    })();
  }, [jobId]);
  // Group interview reports by stageId (when present) or round.
  // This ensures custom pipeline stages like SJT (Arya) get their own group
  // instead of being merged into the generic finalInterview group.
  const ROUND_ORDER = [
    "preScreening", "psychoanalytical", "coding", "gen_ai_readiness", "finalInterview",
  ];
  const DEFAULT_LABEL_FOR_ROUND = {
    preScreening:     "Pre-Screening Interview",
    psychoanalytical: "Psychoanalytical Assessment",
    finalInterview:   "Final Interview",
    coding:           "Coding Test",
    gen_ai_readiness: "Gen AI Readiness Interview",
  };
  // Reverse map: label → round, to resolve old reports that are missing the round field
  const LABEL_TO_ROUND = useMemo(() => Object.fromEntries(
    Object.entries(DEFAULT_LABEL_FOR_ROUND).map(([k, v]) => [v.toLowerCase(), k])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ), []);
  const groups = useMemo(() => {
    const map = {};
    reports.forEach(r => {
      // Resolve round: use stored round, or infer from stageLabel for legacy reports without round
      const round = r.round ||
        (r.stageLabel && LABEL_TO_ROUND[r.stageLabel.toLowerCase()]) ||
        null;
      // Use stageId as key only when the stage has a custom label that differs
      // from the standard round label (e.g. SJT under finalInterview engine).
      // Otherwise group by round so all reports for the same stage merge.
      const isCustomLabel = r.stageId && !r.stageId.startsWith("legacy-") &&
        r.stageLabel && round && DEFAULT_LABEL_FOR_ROUND[round] &&
        r.stageLabel.toLowerCase() !== DEFAULT_LABEL_FOR_ROUND[round].toLowerCase();
      const key = isCustomLabel ? r.stageId : (round || "other");
      if (!map[key]) map[key] = { round: round || "other", label: r.stageLabel || null, reports: [] };
      map[key].reports.push(r);
    });
    // Sort: known rounds first in ROUND_ORDER, then custom stages alphabetically
    const known  = ROUND_ORDER.filter(k => map[k]);
    const custom = Object.keys(map).filter(k => !ROUND_ORDER.includes(k)).sort();
    return [...known, ...custom].map(k => ({ round: map[k].round, label: map[k].label, key: k, reports: map[k].reports }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reports]);
  const totalReports     = reports.length + assessments.reduce((s, g) => s + g.results.length, 0);
  const totalCandidates  = new Set([
    ...reports.map(r => r.resumeId || r.candidateEmail),
    ...assessments.flatMap(g => g.results.map(r => String(r.resumeId))),
  ]).size;
  const totalStages      = groups.length + assessments.length;
  const isEmpty          = reports.length === 0 && assessments.length === 0;
  return (
    <Page>
      {/* ── Header ── */}
      <PageHeader
        title="Assessment Reports"
        subtitle={!loading && totalReports > 0
          ? `${totalReports} report${totalReports !== 1 ? "s" : ""} · ${totalCandidates} candidate${totalCandidates !== 1 ? "s" : ""} · ${totalStages} stage${totalStages !== 1 ? "s" : ""}`
          : "Every interview and exam report for this role, grouped by round."}
        actions={<Button variant="secondary" icon={<Icon name="arrow-left" />} onClick={() => nav(-1)}>Back</Button>}
      />
      {loading && <Card><Loading label="Loading reports" /></Card>}
      {!loading && isEmpty && (
        <Card>
          <EmptyState
            icon={<Icon name="file-text" size={36} />}
            title="No reports yet"
            description="Reports appear here as candidates complete their interview rounds."
          />
        </Card>
      )}
      {!loading && !isEmpty && (
        <>
          <CostBanner jobId={jobId} reports={reports} />
          {/* ── Interview / AI report groups ── */}
          {groups.map((g, i) => (
            <RoundGroup
              key={g.key || g.round}
              round={g.round}
              label={g.label}
              reports={g.reports}
              jobId={jobId}
              nav={nav}
              defaultOpen={i === 0}
            />
          ))}
          {/* ── Exam assessment groups ── */}
          {assessments.length > 0 && (
            <>
              {groups.length > 0 && (
                <h2 style={{ margin: "8px 0 0", fontSize: "var(--fs-body-md)", fontWeight: 600, color: "var(--text-2)" }}>Exam Assessments</h2>
              )}
              {assessments.map((g, i) => (
                <AssessmentGroup key={g.stageId} group={g} defaultOpen={i === 0 && groups.length === 0} />
              ))}
            </>
          )}
        </>
      )}
    </Page>
  );
}
// ─────────────────────────────────────────────────────────────────────
//  Detail view
// ─────────────────────────────────────────────────────────────────────
export function InterviewReportDetail() {
  const { jobId, reportId } = useParams();
  const nav = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/reports/${reportId}`);
        setReport(r.data?.data || null);
      } catch (e) {
        toast.error("Failed to load report");
      } finally {
        setLoading(false);
      }
    })();
  }, [reportId]);
  const printReport = () => window.print();
  if (loading) {
    return <Card><Loading /></Card>;
  }
  if (!report) {
    return <Card><EmptyState icon={<Icon name="file-text" size={36} />} title="Report not found." /></Card>;
  }
  const stage = (report.stageLabel || ROUND_LABEL[report.round] || report.round).replace(/\s*\(Arya\)/gi, '');
  return (
    <Page>
      <style>{PRINT_CSS}</style>
      {/* Header */}
      <PageHeader
        title={report.candidateName}
        subtitle={`${stage} · ${report.jobTitle}${report.candidateEmail ? ` · ${report.candidateEmail}` : ""}`}
        actions={(
          <span className="ir-noprint" style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <Button variant="secondary" icon={<Icon name="arrow-left" />} onClick={() => nav(-1)}>Back</Button>
            <Button icon={<Icon name="download" />} onClick={printReport}>Export PDF</Button>
          </span>
        )}
      />
      <StatRow>
        <Stat label="Overall score" value={<>{report.overallScore ?? "—"}<span style={{ fontSize: "var(--fs-body-sm)", color: "var(--text-muted)", fontWeight: 400 }}> /100</span></>} />
        <Stat label="Recommendation" value={<Badge reco={report.recommendation} />} />
        <Stat label="Interview" value={report.modality === "audio" ? "Audio interview" : "Text interview"} hint={new Date(report.completedAt).toLocaleString()} />
      </StatRow>
      {/* A protected topic the interviewer raised: first, before any score
          (@story HIR-HR21, 30 Oct 2026). */}
      <GuardrailFlags flags={report.guardrailFlags} />
      {/* Feedback, not editing: to EquaOS, with this report (@story HIR-HR21). */}
      <SomethingWrong className="ir-noprint" about="report" reportId={report._id} />
      {/* Round-specific hero — unique parameters per assessment type. A stage
          with a brief is scored on the brief's own lines instead. */}
      {!hasBriefScores(report) && <RoundHero report={report} />}
      {/* No-response warning banner */}
      {report.recommendation === "no_response" && (
        <Note tone="warning">
          <div style={{ fontWeight: 600 }}>Candidate did not respond</div>
          <div style={{ marginTop: 2 }}>
            The candidate connected to the interview but provided no verbal responses. No AI score has been assigned.
            Please follow up with the candidate or re-invite them to complete the interview.
          </div>
        </Note>
      )}
      <BriefRubricScores report={report} />
      {/* Axis scores (a stage without a brief) */}
      {!hasBriefScores(report) && report.axisScores?.length > 0 && (
        <Section title="Axis scores">
          <div style={stack(14)}>
            {report.axisScores.map((a, i) => (
              <div key={i}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                  <div style={{ fontWeight: 500, color: "var(--text-1)" }}>{a.axis}</div>
                  <div className="num" style={{ fontWeight: 600, color: "var(--text-1)" }}>{a.score ?? "—"}</div>
                </div>
                <ScoreBar value={a.score} />
                {a.note && <div style={{ ...muted, marginTop: 4 }}>{a.note}</div>}
              </div>
            ))}
          </div>
        </Section>
      )}
      {/* Strengths / red flags */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(320px, 100%), 1fr))", gap: 20 }}>
        <Section title="Strengths">
          <PointList items={report.strengths} mark="✓" tone="success" />
        </Section>
        <Section title="Red flags">
          <PointList items={report.redFlags} mark="!" tone="danger" />
        </Section>
      </div>
      {/* Selection narrative */}
      {report.selectionNarrative && (
        <Section title="Selection narrative">
          <p style={{ margin: 0, color: "var(--text-2)", fontSize: "var(--fs-body-sm)", lineHeight: "var(--lh-body)", whiteSpace: "pre-line" }}>{report.selectionNarrative}</p>
        </Section>
      )}
      {/* AI Cost */}
      <Section title="AI Cost Incurred">
        <div style={stack(12)}>
          <div style={tiles(150)}>
            <div style={tile}>
              <div style={tileLabel}>Interview cost</div>
              <div style={tileValue}>${(report.cost?.usd || 0).toFixed(4)}</div>
            </div>
            <div style={tile}>
              <div style={tileLabel}>Tokens used</div>
              <div style={tileValue}>{(report.cost?.tokens || 0).toLocaleString()}</div>
            </div>
            <div style={tile}>
              <div style={tileLabel}>Duration</div>
              <div style={tileValue}>{report.durationSec ? `${Math.round(report.durationSec / 60)}m` : "—"}</div>
            </div>
            <div style={tile}>
              <div style={tileLabel}>Modality</div>
              <div style={{ ...tileValue, textTransform: "capitalize" }}>{report.modality || "audio"}</div>
            </div>
          </div>
          <p style={{ ...muted, margin: 0 }}>
            Cost shown is for this interview round only. View the candidate's full report for total AI spend across all stages.
          </p>
        </div>
      </Section>
      {/* Proctoring Report — shown 10 min after interview completes */}
      <ProctoringSection proctoring={report.proctoring} completedAt={report.completedAt} />
      {/* Full transcript */}
      <Section title="Full transcript">
        {report.transcript?.length ? (
          <div className="ir-transcript" style={stack(12)}>
            {report.transcript.map((t, i) => (
              <div key={i} style={{ display: "flex", justifyContent: t.role === "candidate" ? "flex-end" : "flex-start" }}>
                <div style={{
                  maxWidth: "80%", padding: "8px 12px", borderRadius: "var(--radius-md)", fontSize: "var(--fs-body-sm)", color: "var(--text-1)",
                  background: t.role === "candidate" ? "var(--brand-primary-soft)" : "var(--surface-2)",
                }}>
                  <div style={{ ...muted, fontWeight: 600, marginBottom: 4 }}>
                    {t.role === "candidate" ? "Candidate" : "Interviewer"}
                  </div>
                  <div style={{ whiteSpace: "pre-line" }}>{t.text}</div>
                </div>
              </div>
            ))}
          </div>
        ) : <div style={{ color: "var(--text-muted)", fontSize: "var(--fs-body-sm)" }}>No transcript recorded.</div>}
      </Section>
    </Page>
  );
}
function PointList({ items, mark, tone }) {
  if (!items?.length) return <div style={{ color: "var(--text-muted)", fontSize: "var(--fs-body-sm)" }}>None recorded.</div>;
  return (
    <ul style={{ ...stack(8), listStyle: "none", margin: 0, padding: 0 }}>
      {items.map((s, i) => (
        <li key={i} style={{ display: "flex", gap: 8, fontSize: "var(--fs-body-sm)", color: "var(--text-2)" }}>
          <span style={{ color: toneText(tone), fontWeight: 700 }}>{mark}</span><span>{s}</span>
        </li>
      ))}
    </ul>
  );
}
const PROCTOR_DELAY_MS = 10 * 60 * 1000; // 10 minutes
function ProctoringSection({ proctoring, completedAt }) {
  const [now, setNow] = React.useState(Date.now());
  const completed = completedAt ? new Date(completedAt).getTime() : 0;
  const elapsed   = now - completed;
  const ready     = elapsed >= PROCTOR_DELAY_MS;
  const remaining = Math.max(0, PROCTOR_DELAY_MS - elapsed);
  const mins      = Math.floor(remaining / 60000);
  const secs      = Math.floor((remaining % 60000) / 1000);
  // Tick every second while still in the delay window
  React.useEffect(() => {
    if (ready) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [ready]);
  if (!proctoring && ready) return null;   // no data and delay passed — nothing to show
  if (!proctoring && !ready) return null;  // no data yet at all — nothing to show
  if (!ready) {
    return (
      <Section title="Proctoring Report">
        <Note tone="info">
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            <Icon name="spinner" spin />
            <div>
              <div style={{ fontWeight: 600 }}>Processing proctoring data…</div>
              <div style={{ marginTop: 2 }}>
                Available in {mins}m {secs.toString().padStart(2, "0")}s — proctoring analysis runs after a 10-minute review window to ensure it does not affect immediate scoring.
              </div>
            </div>
          </div>
        </Note>
      </Section>
    );
  }
  return <ProctoringReport proctoring={proctoring} />;
}
const RISK_STYLE = {
  low:    { tone: "success", label: "Low risk"  },
  medium: { tone: "warning", label: "Med risk"  },
  high:   { tone: "danger",  label: "High risk" },
};
const SEV_TONE = { high: "danger", medium: "warning", low: "neutral" };
function ProctoringReport({ proctoring: p }) {
  const [showViolations, setShowViolations] = React.useState(false);
  const [selectedScreenshot, setSelectedScreenshot] = React.useState(null);
  const style = RISK_STYLE[p.riskLevel] || RISK_STYLE.low;
  const score = Math.max(0, Math.min(100, p.integrityScore ?? 100));
  return (
    <Section title="Proctoring Report">
      <div style={stack(16)}>
        {/* Score + risk */}
        <div style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 180 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <span style={{ fontSize: "var(--fs-body-sm)", fontWeight: 600, color: "var(--text-2)" }}>Integrity Score</span>
              <StatusPill tone={style.tone} dot={false}>{score}/100 · {style.label}</StatusPill>
            </div>
            <div style={{ width: "100%", height: 8, background: "var(--chart-track)", borderRadius: "var(--radius-pill)", overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${score}%`, background: toneText(style.tone), borderRadius: "var(--radius-pill)" }} />
            </div>
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={tileLabel}>Total violations</div>
            <div className="num" style={{ fontSize: 28, fontWeight: 600, color: toneText(p.totalViolations > 0 ? "danger" : "success") }}>
              {p.totalViolations}
            </div>
          </div>
        </div>
        {/* Event counters */}
        <div style={tiles(140)}>
          {[
            { label: "Tab switches",     value: p.browserEvents?.tabSwitches    || 0 },
            { label: "Focus losses",     value: p.browserEvents?.focusLosses    || 0 },
            { label: "No face detected", value: p.cameraEvents?.noFaceCount     || 0 },
            { label: "Multiple faces",   value: p.cameraEvents?.multiFaceCount  || 0 },
            { label: "Noise events",     value: p.audioEvents?.noiseCount       || 0 },
            { label: "External voices",  value: p.audioEvents?.externalVoiceCount || 0 },
            { label: "Fullscreen exits", value: p.browserEvents?.fullscreenExits || 0 },
            { label: "Window minimize",  value: p.browserEvents?.windowMinimizes || 0 },
          ].map(({ label, value }) => (
            <div key={label} style={tile}>
              <div style={tileLabel}>{label}</div>
              <div className="num" style={{ ...tileValue, color: value > 0 ? "var(--status-danger-text)" : "var(--text-muted)" }}>{value}</div>
            </div>
          ))}
        </div>
        {/* Violation timeline */}
        {(p.violations || []).length > 0 && (
          <div style={stack(8)}>
            <div>
              <Button size="sm" variant="ghost" aria-expanded={showViolations} onClick={() => setShowViolations(v => !v)}>
                {showViolations ? "Hide" : "Show"} violation timeline ({p.violations.length})
              </Button>
            </div>
            {showViolations && (
              <div style={{ ...stack(6), maxHeight: 256, overflowY: "auto" }}>
                {p.violations.map((v, i) => {
                  const tone = SEV_TONE[v.severity] || SEV_TONE.low;
                  return (
                    <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: "var(--fs-caption)", background: `var(--status-${tone}-bg)`, color: `var(--status-${tone}-text)`, borderRadius: "var(--radius-sm)", padding: "8px 12px" }}>
                      <span style={{ fontWeight: 700, textTransform: "capitalize", flex: "none" }}>{(v.type || "").replace(/_/g, " ")}</span>
                      <span style={{ flex: 1, color: "var(--text-2)" }}>{v.description}</span>
                      <span style={{ color: "var(--text-muted)", marginLeft: 8, flex: "none" }}>{v.timestamp ? new Date(v.timestamp).toLocaleTimeString() : ""}</span>
                      {v.screenshot && (
                        <Button size="sm" variant="ghost" icon={<Icon name="eye" />} aria-label="Violation screenshot" onClick={() => setSelectedScreenshot(v.screenshot)} />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
      {/* Screenshot */}
      <Modal
        open={Boolean(selectedScreenshot)}
        title="Violation screenshot"
        width="360px"
        onClose={() => setSelectedScreenshot(null)}
        footer={<Button variant="secondary" onClick={() => setSelectedScreenshot(null)}>Close</Button>}
      >
        {selectedScreenshot && (
          <img src={selectedScreenshot} alt="violation" style={{ width: "100%", borderRadius: "var(--radius-sm)", border: "1px solid var(--border)" }} />
        )}
      </Modal>
    </Section>
  );
}
